
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import type { AdminTask } from '@/types/tasks'

export const useAdminTasks = () => {
  const [adminTasks, setAdminTasks] = useState<AdminTask[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchAdminTasks = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setLoading(false)
        return
      }

      // Get user's school_id from their profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('school_id')
        .eq('id', session.user.id)
        .single()

      let allTasks: AdminTask[] = []

      // Fetch global admin tasks (school_id is null)
      const { data: globalTasks, error: globalError } = await supabase
        .from('admin_tasks')
        .select('*')
        .eq('type', 'discover')
        .is('school_id', null)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })

      if (globalError) throw globalError
      allTasks = [...(globalTasks || [])]

      // Fetch school-specific admin tasks if user has a school
      if (profile?.school_id) {
        const { data: schoolTasks, error: schoolError } = await supabase
          .from('admin_tasks')
          .select('*')
          .eq('type', 'discover')
          .eq('school_id', profile.school_id)
          .order('priority', { ascending: false })
          .order('created_at', { ascending: false })

        if (schoolError) throw schoolError
        allTasks = [...allTasks, ...(schoolTasks || [])]
      }

      setAdminTasks(allTasks)
    } catch (error: any) {
      console.error('Error fetching admin tasks:', error)
      toast({
        title: "Error",
        description: "Failed to load admin tasks",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAdminTasks()

    // Set up real-time subscription
    const channel = supabase
      .channel('admin-tasks-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_tasks'
        },
        () => {
          fetchAdminTasks()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [])

  return {
    adminTasks,
    loading,
    refreshAdminTasks: fetchAdminTasks
  }
}
